import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import MDBox from '../../components/MDBox';
import MDTypography from '../../components/MDTypography';

function GapSlider({ 
    value, 
    onChange, 
    min = 0, 
    max = 100, 
    step = 1, 
    marks = [], 
    disabled = false,
    label = "Value"
}) {
    const [isDragging, setIsDragging] = useState(false);
    const [dragPosition, setDragPosition] = useState(null);
    const sliderRef = useRef(null);
    const thumbRef = useRef(null);

    const calculatePosition = (value) => {
        return ((value - min) / (max - min)) * 100;
    };

    const calculateValue = (position) => {
        const percentage = position / 100;
        const rawValue = percentage * (max - min) + min;
        return Math.round(rawValue / step) * step;
    };

    const handleMouseDown = (e) => {
        if (disabled) return;
        setIsDragging(true);
        updatePosition(e);
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        updatePosition(e);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setDragPosition(null);
    };

    const updatePosition = (e) => {
        if (!sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        let position = ((e.clientX - rect.left) / rect.width) * 100;
        position = Math.max(0, Math.min(100, position));
        
        setDragPosition(position);
        const newValue = calculateValue(position);
        if (newValue !== value) {
            onChange(newValue);
        }
    };

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    const currentPosition = dragPosition !== null ? dragPosition : calculatePosition(value);

    return (
        <MDBox sx={{ width: '100%', px: 2 }}>
            <MDTypography variant="body2" gutterBottom>
                {label}: {value}%
            </MDTypography>
            <MDBox
                ref={sliderRef}
                sx={{
                    position: 'relative',
                    height: '4px',
                    backgroundColor: '#bfbfbf',
                    borderRadius: '2px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                }}
                onClick={(e) => !disabled && updatePosition(e)}
            >
                <MDBox
                    sx={{
                        position: 'absolute',
                        height: '100%',
                        backgroundColor: '#2B5587',
                        borderRadius: '2px',
                        width: `${currentPosition}%`,
                    }}
                />
                <MDBox
                    ref={thumbRef}
                    sx={{
                        position: 'absolute',
                        width: '16px',
                        height: '16px',
                        backgroundColor: '#ffffff',
                        border: '2px solid #2B5587',
                        borderRadius: '50%',
                        top: '50%',
                        left: `${currentPosition}%`,
                        transform: 'translate(-50%, -50%)',
                        cursor: disabled ? 'not-allowed' : 'grab',
                        '&:hover': {
                            boxShadow: disabled ? 'none' : '0 0 0 4px rgba(43, 85, 135, 0.1)',
                        },
                    }}
                    onMouseDown={handleMouseDown}
                />
                {marks.map((mark) => (
                    <MDBox
                        key={mark.value}
                        sx={{
                            position: 'absolute',
                            width: '2px',
                            height: '8px',
                            backgroundColor: '#bfbfbf',
                            top: '-2px',
                            left: `${calculatePosition(mark.value)}%`,
                        }}
                    />
                ))}
            </MDBox>
            {marks.length > 0 && (
                <MDBox sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    {marks.map((mark) => (
                        <MDTypography key={mark.value} variant="caption" color="text.secondary">
                            {mark.label}
                        </MDTypography>
                    ))}
                </MDBox>
            )}
        </MDBox>
    );
}

GapSlider.propTypes = {
    value: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    marks: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.number,
        label: PropTypes.string
    })),
    disabled: PropTypes.bool,
    label: PropTypes.string
};

export default GapSlider; 